import { I18n } from "@/i18n/I18n";

/**
 * Ported verbatim from veriform-desktop, `cleanUpError`
 * (plugins/pluginchildprocessmanager.ts:286).
 *
 * The workflow engine's errors arrive wrapped in layers of Go formatting, and
 * this peels them off. Desktop's own comment on it is a TODO to fix the engine
 * so none of this is needed; until that happens both apps have to unwrap the
 * same strings the same way, or the message stored against a failed task
 * differs depending on which app ran it.
 *
 * The order of the steps is load-bearing -- `replaceAll` on the `map[code:...
 * message:` prefix runs before the checks that look for substrings it removes
 * -- so this is kept in desktop's exact sequence rather than tidied.
 *
 * Desktop stores only `error` against the task and shows `stackTrace` in a
 * toast. This repo has one statusMessage column and no desktop toast, so the
 * stack trace is returned for the caller to use and currently dropped, which
 * leaves what is *stored* identical in both.
 */

/**
 * Desktop's `isUUID` defaults to version 4 (lib/validations/isUUID.ts), which is
 * stricter than this repo's `REGEX.UUID`. Kept local so the two agree: the
 * looser one would strip a leading non-v4 UUID that desktop leaves in place.
 */
const UUID_V4 = /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

export interface CleanedError {
  error: string;
  stackTrace: string;
}

export function cleanUpError(error: string): CleanedError {
  let errorToClean = error;

  if (error.startsWith('{') && error.endsWith('}')) {
    errorToClean = errorToClean.slice(1, -1);
  }

  if (UUID_V4.test(errorToClean.split(' ')[0])) {
    errorToClean = errorToClean.split(' ').slice(1).join(' ');
  }

  if (errorToClean.startsWith('0')) {
    errorToClean = errorToClean.split(' ').slice(1).join(' ');
  }

  errorToClean = errorToClean.replaceAll('map[code:%!s(float64=-1) message:', '');
  errorToClean = errorToClean.replaceAll(']', '');

  if (errorToClean.includes('TypeError')) {
    errorToClean = errorToClean.split('message:')[1].replace('()', '').trim();
  }

  if (errorToClean.includes('map has no entry')) {
    const match = error.match(/<([^>]+)>/);
    const parameterName = match ? match[1] : 'unknown';
    return {
      error: I18n.get('interpolatedValueDoesNotExist', { parameter: `{{${parameterName}}}` }),
      stackTrace: '',
    };
  }

  if (errorToClean.includes('Missing required parameter(s):')) {
    const missingParameters = errorToClean.split('Missing required parameter(s):')[1].trim();
    return {
      error: I18n.get('missingRequiredInputs', { parameters: missingParameters }),
      stackTrace: '',
    };
  }

  if (errorToClean.startsWith('4')) {
    errorToClean = errorToClean.split(' ').slice(1).join(' ');
  }

  if (errorToClean.startsWith('provider responded with an error:')) {
    errorToClean = errorToClean.split('provider responded with an error:')[1].trim();
  }

  if (errorToClean.startsWith('Provider responded with an error code:')) {
    errorToClean = errorToClean.split('Provider responded with an error code:')[1].trim();
  }

  const [errorMessage, stackTrace] = errorToClean.split('Stack trace:');

  const finalStackTrace = stackTrace ? `Stack trace: ${stackTrace.trim()}` : '';

  return { error: errorMessage.trim(), stackTrace: finalStackTrace };
}
